<script lang="ts">
    import { t } from 'svelte-i18n';
    import type { ShopwareLineItem } from '$lib/api/types/additionalShopwareTypes.js';
    import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
    import type { ApiRequestParams } from '$lib/api/shopwareApi.js';
    import { CartApi } from '$lib/api/cartApi.js';
    import { toast } from '$lib/stores.js';
    import { useShopContext, useStoreContext } from '$lib/context.js';
    import { formatCurrency } from '$lib/util/currencyUtil.js';
    import { number } from 'svelte-i18n';
    import { slide } from 'svelte/transition';
    import { ProductApi } from '$lib/api/productApi';
    import CustomSelect from '$lib/components/util/CustomSelect.svelte';
    import R2Image from '$lib/components/util/R2Image.svelte';
    // import IconTrashCan from '~icons/fa6-solid/trash-can';
    // import IconCheck from '~icons/custom/icon-tick';
    // import IconX from '~icons/custom/icon-x';
    // import IconTriangleExclamation from '~icons/fa6-solid/triangle-exclamation';

    const shopContext = useShopContext();
    const storeContext = useStoreContext();
    const queryClient = useQueryClient();

    export let lineItem: ShopwareLineItem;
    export let confirmationPage = false;

    $: productQuery = createQuery({
        queryKey: ['products', lineItem.referencedId],
        queryFn: ({ queryKey }) =>
            ProductApi.getProduct(queryKey[1], shopContext, {
                associations: {
                    options: { associations: { group: {} } },
                    properties: { associations: { group: {} } }
                }
            }),
        staleTime: 30 * 1000,
        gcTime: 120 * 1000
    });
    $: product = $productQuery.data;

    // TODO: reformat to new standard
    // properties
    $: type = product?.properties.find((property: any) =>
        ['type', 'type'].includes((property.group.name ?? property.group.translated.name).toLowerCase())
    )?.translated.name;
    $: taste = product?.properties.find((property: any) =>
        ['geschmack', 'taste'].includes((property.group.name ?? property.group.translated.name).toLowerCase())
    )?.translated.name;

    //options
    $: size = product?.options.find((property: any) =>
        ['größe', 'size'].includes((property.group.name ?? property.group.translated.name).toLowerCase())
    )?.translated.name;
    $: year = product?.options.find((property: any) =>
        ['jahrgang', 'year'].includes((property.group.name ?? property.group.translated.name).toLowerCase())
    )?.translated.name;

    const updateLineItemMutation = createMutation<void, unknown, ApiRequestParams<'updateLineItem'>>({
        mutationFn: (data) => CartApi.updateLineItem(data, shopContext),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] })
    });

    async function updateLineItem(selected) {
        try {
            await $updateLineItemMutation.mutateAsync({
                items: [
                    {
                        id: lineItem.id,
                        referencedId: lineItem.referencedId,
                        quantity: selected.value
                    }
                ]
            });
        } catch (error) {
            $toast.showError($t('shop.error.genericProblem'));
        }
    }

    const removeLineItemMutation = createMutation<void, unknown, ApiRequestParams<'removeLineItem'>>({
        mutationFn: (data) => CartApi.removeLineItem(data, shopContext),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] })
    });

    async function removeLineItem() {
        try {
            await $removeLineItemMutation.mutateAsync({
                ids: [lineItem.id]
            });

            $toast.showInfo($t('shop.cart.productRemoved'));
        } catch (error) {
            $toast.showError($t('shop.error.genericProblem'));
        }
    }
</script>

<div class="relative flex bg-white w-full px-8 py-6" transition:slide>
    {#if lineItem.payload.isCloseout}
        <div class="absolute top-0 left-0 w-full h-full bg-neutral-100/50 z-10" />
    {/if}
    <div class="flex gap-6 w-full">
        <div class="flex justify-center max-h-48 w-full max-w-12">
            {#if lineItem.cover?.url}
                <div class="h-full flex items-end">
                    <R2Image
                        imageId={lineItem.cover.url}
                        widths={[50, 100]}
                        sizes="(max-width: 900px) 100vw, 20vw"
                        alt={lineItem.cover.alt}
                        loading="eager"
                        decoding="sync"
                        class="object-contain min-w-6 h-40 sm:h-32"
                    />
                </div>
            {/if}
        </div>
        <div class="flex justify-between flex-col flex-grow">
            <div class={confirmationPage ? '' : 'mb-2'}>
                {#if product?.translated?.customFields?.custom_lewo_erp_category === 'Wein'}
                    <p class="text-xs text-neutral-500 tracking-widest uppercase">
                        {type ?? '...'} | {taste ?? '...'} <span class="font-light">{size ?? ''}</span>
                    </p>
                {/if}
                <div class="flex justify-between w-full">
                    <h3 class="uppercase font-light text-2xl lg:text-3xl">
                        {lineItem.label}
                        {year && year.toLowerCase() !== 'unbekannt' && year.toLowerCase() !== 'unknown' ? year : ''}
                    </h3>
                    {#if confirmationPage}
                        <span>x{lineItem.quantity}</span>
                    {/if}
                </div>
            </div>
            <div class="flex justify-between items-end">
                {#if !confirmationPage}
                    <!-- maxPurchase + 1 because the max in range(min, max, steps) is exclusive -->
                    <div
                        class="flex flex-col lg:flex-row gap-3 lg:justify-between lg:gap-4 items-start lg:items-center sm:min-w-36"
                    >
                        {#if $updateLineItemMutation.isPending}
                            <div class="border flex justify-center h-[40px] border-primary px-4">
                                <span class="loading loading-spinner loading-sm text-primary"></span>
                            </div>
                        {:else}
                            <CustomSelect
                                class="border border-primary "
                                selected={{ value: lineItem.quantity, label: lineItem.quantity.toString() }}
                                onSelectedChange={updateLineItem}
                                options={Array.from(
                                    {
                                        length: lineItem.quantityInformation.maxPurchase
                                    },
                                    (_, i) => i + 1
                                )}
                                disabled={$updateLineItemMutation.isPending}
                            />
                        {/if}
                        {#if lineItem.removable && !confirmationPage}
                            <button
                                on:click={removeLineItem}
                                class="z-20 text-xs lg:text-sm h-fit py-1 border-b border-neutral-500 text-neutral-500"
                            >
                                {$t('general.remove')}
                            </button>
                        {/if}
                    </div>
                {/if}
                <div class="h-full flex flex-col justify-end text-right">
                    {#if lineItem.payload.isCloseout}
                        <h2 class="text-lg text-accent z-20">{$t('shop.cart.not-stored')}</h2>
                    {:else}
                        <p class="text-xs lg:text-sm">
                            {lineItem.quantity} x {formatCurrency($number, storeContext, lineItem.price.unitPrice)}
                        </p>
                        <h2 class="text-xl lg:text-2xl text-secondary">
                            {formatCurrency($number, storeContext, lineItem.price.unitPrice * lineItem.quantity)}
                        </h2>
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>
